import { Relation } from '@cotera/era';
import { pick } from 'lodash';
import { type ChatTriggerSource, type ResourceType } from '@cotera/api';

/** Stale window for cached `resource.list` / folder-items slices backing Coco list pages. */
export const RESOURCE_LIST_FETCH_STALE_MS = 30_000 as const;

export const queryKeys = {
  chat: {
    conversations(params: {
      orgId: string;
      userId?: string;
      agentIdentifier?: string;
      limit?: number;
    }) {
      return [
        params.orgId,
        'chat',
        'conversations',
        ...(params.userId ? [params.userId] : []),
        ...(params.agentIdentifier ? [params.agentIdentifier] : []),
        ...(params.limit !== undefined ? [params.limit] : []),
      ];
    },
    messages(params: { orgId: string; conversationId: string }) {
      return [params.orgId, 'chat', 'messages', params.conversationId];
    },
    status(params: { orgId: string; conversationId: string }) {
      return [params.orgId, 'chat', 'status', params.conversationId];
    },
  },
  artifacts: {
    usableCache(params: { orgId: string; freshnessMs?: number }) {
      return [
        params.orgId,
        'usableCache',
        ...(params.freshnessMs !== undefined ? [params.freshnessMs] : []),
      ];
    },
    requestById(params: { orgId: string; reqId: string }) {
      return [params.orgId, 'artifactRequests', params.reqId];
    },
    requests(params: { orgId: string; page: number; sqlHash?: string }) {
      return [
        params.orgId,
        'artifactRequests',
        ...(params.sqlHash ? [params.sqlHash] : ['all']),
        { page: params.page },
      ];
    },
    forRel(params: { orgId: string; rel: Relation }) {
      return [params.orgId, 'relation', params.rel.eraqlHash()];
    },
    forEraQL(params: { orgId: string; eraQL: string }) {
      return [params.orgId, 'relation', params.eraQL];
    },
    data(params: { orgId: string; rel: Relation }) {
      return [params.orgId, 'data', params.rel.eraqlHash()];
    },
    dataForEraQL(params: { orgId: string; eraQL: string }) {
      return [params.orgId, 'data', params.eraQL];
    },
    artifact(params: { orgId: string; rel: Relation }) {
      return [params.orgId, 'artifact', params.rel.eraqlHash()];
    },
  },
  workspaceDomain: {
    resource(params: { orgId: string; type: ResourceType; id: string }) {
      return [
        params.orgId,
        'workspaceDomain',
        'resource',
        params.type,
        params.id,
      ] as const;
    },
    folderItems(params: { orgId: string; folderId: string }) {
      return [
        params.orgId,
        'workspaceDomain',
        'folderItems',
        params.folderId,
      ] as const;
    },
  },
  user: {
    whoami() {
      return 'whoami' as const;
    },
  },
  warehouse: {
    keywordConfig(params: { orgId: string }) {
      return [params.orgId, 'keyword-config'];
    },
    intentSignalNodes(params: { orgId: string }) {
      return [params.orgId, 'intent-signal-nodes'];
    },
    sqlInWarehouseDialect(params: { orgId: string; rel: Relation }) {
      return [params.orgId, 'sql-in-warehouse-dialect', params.rel.eraHash()];
    },
    irForSqlHash(params: { orgId: string; sqlHash: string }) {
      return [params.orgId, 'ir', params.sqlHash];
    },
    getPrimary(params: { orgId: string }) {
      return [params.orgId, 'warehouse', 'primaryConnection'] as const;
    },
  },
  sample: {
    base(params: { orgId: string }) {
      return [params.orgId, 'definitions'] as const;
    },
    get(params: { orgId: string; rel: Relation }) {
      return [...this.base(params), 'sample', params.rel.sqlHash()] as const;
    },
  },
  workbook: {
    base(params: { orgId: string }) {
      return [params.orgId, 'workbooks'] as const;
    },
    all(params: { orgId: string }) {
      return [...this.base(params), 'all'] as const;
    },
    published(params: { id: string }) {
      return [params.id, 'workbooks', 'published'] as const;
    },
    publishedById(params: { id: string }) {
      return [params.id, 'workbooks', 'published', 'byId'] as const;
    },
    byId(params: { orgId: string; id: string }) {
      return [...this.base(params), 'workbook', params.id] as const;
    },
  },
  jobs: {
    base(params: { orgId: string }) {
      return [params.orgId, 'jobs'] as const;
    },
    runs(params: { orgId: string; names: string[] }) {
      return [...this.base(params), ...params.names, 'runs'] as const;
    },
  },
  connections: {
    all(params: { orgId: string }) {
      return [params.orgId, 'connections'] as const;
    },
  },
  tools: {
    all(params: { orgId: string }) {
      return [params.orgId, 'tools'] as const;
    },
  },
  toolsV2: {
    all(params: { orgId: string }) {
      return [params.orgId, 'toolsV2'] as const;
    },
    providers(params: { orgId: string }) {
      return [params.orgId, 'toolsV2', 'providers'] as const;
    },
    forProvider(params: { orgId: string; provider: string }) {
      return [params.orgId, 'toolsV2', 'provider', params.provider] as const;
    },
    credentialInfo(params: { orgId: string; toolKey: string }) {
      return [
        params.orgId,
        'toolsV2',
        'credentialInfo',
        params.toolKey,
      ] as const;
    },
    providerAccounts(params: { orgId: string; provider: string }) {
      return [
        params.orgId,
        'toolsV2',
        'providerAccounts',
        params.provider,
      ] as const;
    },
    allProviders(params: { orgId: string }) {
      return [params.orgId, 'toolsV2', 'allProviders'] as const;
    },
    workosDataProviders(params: { orgId: string }) {
      return [params.orgId, 'toolsV2', 'workosDataProviders'] as const;
    },
    slackBotInstallation(params: { orgId: string }) {
      return [params.orgId, 'toolsV2', 'slackBotInstallation'] as const;
    },
    linearBotInstallation(params: { orgId: string }) {
      return [params.orgId, 'toolsV2', 'linearBotInstallation'] as const;
    },
  },
  topics: {
    versions(params: { orgId: string; topicModelId: string }) {
      return [params.orgId, 'topics', 'versions', params.topicModelId] as const;
    },
    topics(params: { versionId: string; topicModelId: string; orgId: string }) {
      return [
        params.orgId,
        'topics',
        params.versionId,
        params.topicModelId,
      ] as const;
    },
    suggestedTopics(params: {
      topicModelId: string;
      versionId: string;
      orgId: string;
    }) {
      return [
        params.orgId,
        'suggestedTopics',
        params.topicModelId,
        params.versionId,
      ] as const;
    },
    detectedFeatures(props: {
      orgId: string;
      topicModelId: string;
      versionId: string;
      search?: string;
    }) {
      return [
        props.orgId,
        'nlp',
        'features',
        props.topicModelId,
        props.versionId,
      ];
    },
    searchDetectedFeatures(props: {
      orgId: string;
      topicModelId: string;
      versionId: string;
      search?: string;
    }) {
      return [
        props.orgId,
        'nlp',
        'features',
        props.topicModelId,
        props.versionId,
        props.search,
      ];
    },
  },
  manifest: {
    detailPage(params: { orgId: string; page: string; version: string }) {
      return [params.orgId, 'detailPage', params.version, params.page];
    },
    manifestMeta(params: { orgId: string; source: 'commited' | 'dev' }) {
      return [params.orgId, 'manifestMeta', params.source];
    },
    def(params: { orgId: string; id: string; version: string }) {
      return [
        params.orgId,
        'manifest',
        params.version,
        'definition',
        pick(params, ['id', 'version']),
      ];
    },
    app(params: { orgId: string; id: string; version: string }) {
      return [
        params.orgId,
        'manifest',
        params.version,
        'app',
        pick(params, ['id', 'version']),
      ];
    },
    local(params: { orgId: string; enabled: boolean }) {
      return [params.orgId, 'manifest', 'local', params.enabled] as const;
    },
    commited(params: { orgId: string }) {
      return [params.orgId, 'manifest', 'commited'] as const;
    },
  },
  ideConfig(params: { orgId: string }) {
    return [params.orgId, 'ideConfig'];
  },
  entity: {
    columns(params: { orgId: string; entityId: string }) {
      return [params.orgId, 'entity', params.entityId, 'columns'];
    },
    list(params: { orgId: string }) {
      return [params.orgId, 'entities', 'list'];
    },
  },
  integrations(params: { orgId: string }) {
    return [params.orgId, 'integrations'];
  },
  agents: {
    /**
     * Shared by several hooks (display names, id-by-key, agent options). React
     * Query dedupes by key, so all consumers MUST go through `useAgentsQuery`
     * and read its `AgentsListData` shape — do NOT add a second `useQuery` on
     * this key with a narrower `queryFn`. Only the first query to mount wins the
     * cache; a divergent shape would be silently dropped (this caused agent
     * model/description to go missing — COT-6252). Need more fields? Widen
     * `AgentsListData` in use-agents-display-names.ts.
     */
    all(params: { orgId: string }) {
      return [params.orgId, 'agents', 'all'] as const;
    },
    byId(params: { orgId: string; id: string }) {
      return [params.orgId, 'agents', params.id] as const;
    },
  },
  workflow: {
    scheduledAgentBundle(params: { orgId: string; workflowId: string }) {
      return [
        params.orgId,
        'workflow',
        'scheduledAgentBundle',
        params.workflowId,
      ] as const;
    },
    triggerWorkflows(params: { orgId: string; triggerId: string }) {
      return [
        params.orgId,
        'workflow',
        'triggerWorkflows',
        params.triggerId,
      ] as const;
    },
    datasetAutomationWorkflows(params: { orgId: string; datasetId: string }) {
      return [
        params.orgId,
        'workflow',
        'datasetAutomationWorkflows',
        params.datasetId,
      ] as const;
    },
    runs(params: {
      orgId: string;
      workflowId: string;
      limit: number;
      filterKey?: string;
    }) {
      return [
        params.orgId,
        'workflow',
        'runs',
        params.workflowId,
        params.limit,
        params.filterKey ?? '',
      ] as const;
    },
    /**
     * Runs for many workflows inside one window — the schedule view's batch fetch.
     * Keyed on the joined id list so a changed set of triggers refetches, and on
     * the window bounds so paging weeks doesn't reuse the previous week's rows.
     */
    runsByWorkflows(params: {
      orgId: string;
      workflowIds: readonly string[];
      createdAfter: string;
      createdBefore: string;
    }) {
      return [
        params.orgId,
        'workflow',
        'runsByWorkflows',
        params.workflowIds.join(','),
        params.createdAfter,
        params.createdBefore,
      ] as const;
    },
    run(params: { orgId: string; workflowId: string; runId: string }) {
      return [
        params.orgId,
        'workflow',
        'run',
        params.workflowId,
        params.runId,
      ] as const;
    },
    runSteps(params: { orgId: string; workflowId: string; runId: string }) {
      return [
        params.orgId,
        'workflow',
        'runSteps',
        params.workflowId,
        params.runId,
      ] as const;
    },
    runGraph(params: { orgId: string; workflowId: string; runId: string }) {
      return [
        params.orgId,
        'workflow',
        'runGraph',
        params.workflowId,
        params.runId,
      ] as const;
    },
    runStepPayload(params: {
      orgId: string;
      workflowId: string;
      runId: string;
      nodeId: string;
    }) {
      return [
        params.orgId,
        'workflow',
        'runStepPayload',
        params.workflowId,
        params.runId,
        params.nodeId,
      ] as const;
    },
    triggers(params: { orgId: string; workflowId: string }) {
      return [params.orgId, 'workflow', 'triggers', params.workflowId] as const;
    },
    events(params: { orgId: string }) {
      return [params.orgId, 'workflow', 'events'] as const;
    },
  },
  resource: {
    get(params: { orgId: string; type: ResourceType; id: string }) {
      return [params.orgId, 'resource', 'get', params.type, params.id] as const;
    },
    /** Who can reach a resource and how, for the share dialog. */
    access(params: { orgId: string; type: ResourceType; id: string }) {
      return [
        params.orgId,
        'resource',
        'access',
        params.type,
        params.id,
      ] as const;
    },
    /** Live share links on a resource ("Manage links"). */
    shareLinks(params: { orgId: string; type: ResourceType; id: string }) {
      return [
        params.orgId,
        'resource',
        'shareLinks',
        params.type,
        params.id,
      ] as const;
    },
    list(params: {
      orgId: string;
      type: ResourceType;
      page?: number;
      limit?: number;
      folderScope?: 'private' | 'shared';
    }) {
      return [
        params.orgId,
        'resource',
        'list',
        params.type,
        ...(params.page !== undefined ? [params.page] : []),
        ...(params.limit !== undefined ? [params.limit] : []),
        ...(params.folderScope ? [params.folderScope] : []),
      ] as const;
    },
    /**
     * One page of `resource.list` (`limit`/`offset`/filters). Prefer this over {@link queryKeys.resource.list}
     * for list UIs where search/sort/offset matter.
     */
    listPage(params: {
      orgId: string;
      type: ResourceType;
      limit: number;
      offset: number;
      search?: string;
      sort?: string;
      sortDir?: 'asc' | 'desc';
      folderScope?: 'private' | 'shared';
      ownership?: 'mine' | 'shared';
      agentId?: string;
      triggerSources?: ChatTriggerSource[];
      triggerKind?: 'cron' | 'webhook' | 'event' | 'dataset' | 'none';
      /**
       * Chat lists filtered to unread assistant messages. Part of the key
       * because it changes the response: without it the filtered and unfiltered
       * pages share one entry, so toggling the filter re-read the cached rows
       * and only a reload — which starts with an empty cache — ever showed it
       * working. `folderContents` has always keyed on it; this did not.
       */
      unreadOnly?: boolean;
      /** Sidebar "Unfiled" views: only chats with no folder. Same reasoning. */
      unfiledOnly?: boolean;
    }) {
      return [
        params.orgId,
        'resource',
        'listPage',
        params.type,
        params.limit,
        params.offset,
        params.search ?? '',
        params.sort ?? '',
        params.sortDir ?? '',
        params.folderScope ?? '',
        params.agentId ?? '',
        ...(params.triggerSources !== undefined &&
        params.triggerSources.length > 0
          ? [params.triggerSources.join(',')]
          : ['']),
        params.triggerKind ?? '',
        params.ownership ?? '',
        ...(params.unreadOnly === true ? ['unread'] : []),
        ...(params.unfiledOnly === true ? ['unfiled'] : []),
      ] as const;
    },
    folderList(params: {
      orgId: string;
      folderScope?: 'private' | 'shared';
      limit?: number;
    }) {
      return [
        params.orgId,
        'resource',
        'folderList',
        ...(params.folderScope ? [params.folderScope] : []),
        ...(params.limit !== undefined ? [params.limit] : []),
      ] as const;
    },
    /** Full folder tree + unfiled items (`resource/folder/list`). */
    folderTree(params: { orgId: string }) {
      return [params.orgId, 'resource', 'folder', 'tree'] as const;
    },
    folderDescendantIds(params: { orgId: string; folderId: string }) {
      return [
        params.orgId,
        'resource',
        'folder',
        'descendantIds',
        params.folderId,
      ] as const;
    },
    folderContents(params: {
      orgId: string;
      folderId: string;
      folderScope?: 'private' | 'shared';
      itemTypes?: Array<ResourceType>;
      search?: string;
      sort?: string;
      sortDir?: 'asc' | 'desc';
      limit?: number;
      offset?: number;
      unreadOnly?: boolean;
      triggerSources?: ChatTriggerSource[];
    }) {
      return [
        params.orgId,
        'resource',
        'folderContents',
        params.folderId,
        ...(params.folderScope !== undefined ? [params.folderScope] : []),
        ...(params.itemTypes !== undefined && params.itemTypes.length > 0
          ? [params.itemTypes.join(',')]
          : []),
        ...(params.search !== undefined && params.search !== ''
          ? [params.search]
          : []),
        ...(params.sort !== undefined ? [params.sort] : []),
        ...(params.sortDir !== undefined ? [params.sortDir] : []),
        ...(params.limit !== undefined ? [params.limit] : []),
        ...(params.offset !== undefined ? [params.offset] : []),
        ...(params.unreadOnly === true ? ['unread'] : []),
        ...(params.triggerSources !== undefined &&
        params.triggerSources.length > 0
          ? [params.triggerSources.join(',')]
          : []),
      ] as const;
    },
    /**
     * Server-side Library root (`resource/folder/library-root`): root folders +
     * unfiled artifacts, ownership-filtered and paginated. Invalidated together
     * with `folderContents` on any resource update.
     */
    libraryRoot(params: {
      orgId: string;
      ownership?: 'mine' | 'shared';
      search?: string;
      sort?: string;
      sortDir?: 'asc' | 'desc';
      limit?: number;
      offset?: number;
    }) {
      return [
        params.orgId,
        'resource',
        'libraryRoot',
        params.ownership ?? 'all',
        ...(params.search !== undefined && params.search !== ''
          ? [params.search]
          : []),
        ...(params.sort !== undefined ? [params.sort] : []),
        ...(params.sortDir !== undefined ? [params.sortDir] : []),
        ...(params.limit !== undefined ? [params.limit] : []),
        ...(params.offset !== undefined ? [params.offset] : []),
      ] as const;
    },
    /**
     * The calling user's favorites (`resource/favorites`), in their order.
     *
     * One key per org and nothing else: favorites are per-user and unpaginated,
     * so the sidebar list, the star on a Library row and the star in a page
     * header all read the same cached array. Invalidate it after any favorite
     * write — it is the only thing that has to be refetched, since no other
     * list's contents change when something is starred.
     */
    favorites(params: { orgId: string }) {
      return [params.orgId, 'resource', 'favorites'] as const;
    },
    /** Top-level unfiled counts for virtual group rows (`resource/folder/getUnfiledTotals`). */
    unfiledTotals(params: {
      orgId: string;
      folderScope?: 'private' | 'shared';
    }) {
      return [
        params.orgId,
        'resource',
        'unfiledTotals',
        params.folderScope ?? 'all',
      ] as const;
    },
    /** `resource.agent.getMentionedToolsStatus` — owner-org tool config for agent @mentions. */
    agentMentionedToolsStatus(params: {
      orgId: string;
      agentId: string;
      agentUpdatedAt?: string;
    }) {
      return [
        params.orgId,
        'resource',
        'agent',
        'mentionedToolsStatus',
        params.agentId,
        ...(params.agentUpdatedAt !== undefined ? [params.agentUpdatedAt] : []),
      ] as const;
    },
  },
  webhookProviders: {
    catalog(params: { orgId: string }) {
      return [params.orgId, 'webhookProviders', 'catalog'] as const;
    },
  },
  webhookTriggers: {
    settings(params: { orgId: string; triggerId: string }) {
      return [
        params.orgId,
        'webhookTriggers',
        'settings',
        params.triggerId,
      ] as const;
    },
  },
  settings: {
    org: {
      plan(orgId: string) {
        return [orgId, 'settings', 'org', 'plan'];
      },
    },
    billing: {
      all: (orgId: string) => [orgId, 'billing-settings'],
      credit_balance: (orgId: string) => [
        orgId,
        'billing-settings',
        'balance-info',
      ],
      billingSettingsInfo(params: { orgId: string }) {
        return [params.orgId, 'billing-settings', 'info'];
      },
    },
  },
};
